import { TextError } from '../form';
import { Disclosure } from '../';
import { ErrorMessage } from '@hookform/error-message';
import { cn } from '@workspace/utils';
import { addDays, addMinutes, format, getDay, isAfter, setHours, setMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * Calendrier de sélection de créneaux horaires
 * - Génère automatiquement les 5 prochains jours ouvrés
 * - Créneaux de 30min entre 9h et 18h
 * - Exclut les créneaux passés pour aujourd'hui
 * - Interface accordéon avec couleurs custom
 */
export function RadioCalendar() {
  const methods = useFormContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = new Date();

  const dateAndTime = useMemo(() => {
    // Générer les 5 prochains jours ouvrés
    const nextDates = [];
    let currentDate = today;
    while (nextDates.length < 5) {
      currentDate = addDays(currentDate, 1);
      if (getDay(currentDate) !== 0) { // Exclure dimanche
        nextDates.push(currentDate);
      }
    }

    // Créer les créneaux de 30min (9h-18h)
    const startTime = setMinutes(setHours(today, 9), 0);
    const endTime = setMinutes(setHours(today, 18), 0);
    const availableTime: string[] = [];
    let currentTime = startTime;
    
    while (isAfter(endTime, currentTime) || currentTime.getTime() === endTime.getTime()) {
      availableTime.push(
        format(currentTime, 'HH:mm') + ' - ' + format(addMinutes(currentTime, 30), 'HH:mm')
      );
      currentTime = addMinutes(currentTime, 30);
    }

    const dateAndTime = nextDates.map(date => ({
      date,
      availableTime: [...availableTime],
    }));

    // Filtrer les créneaux passés pour aujourd'hui
    const todayAvailableTime = availableTime.filter(time => {
      const hour = setMinutes(setHours(today, parseInt(time.split(':')[0])), parseInt(time.split(':')[1]));
      return isAfter(hour, today);
    });

    dateAndTime.unshift({
      date: today,
      availableTime: todayAvailableTime,
    });

    return dateAndTime;
  }, [today]);

  const handleClick = (date: Date, heure: string) => {
    methods.setValue('date', format(date, 'dd/MM/yyyy'));
    methods.setValue('heure', heure);
    methods.clearErrors('date');
    methods.trigger();
  };

  return (
    <div className="space-y-4">
      {dateAndTime.map(dateSlot => (
        <Disclosure
          title={format(dateSlot.date, 'EEEE d MMMM', { locale: fr })}
          key={dateSlot.date.toISOString()}
          isOpen={methods.watch('date') === format(dateSlot.date, 'dd/MM/yyyy')}
        >
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 text-center gap-2">
            {dateSlot.availableTime.map(time => (
              <li
                key={`${dateSlot.date.toISOString()}-${time}`}
                onClick={() => handleClick(dateSlot.date, time)}
                className={cn(
                  'rounded-lg px-4 py-2 hover:bg-primary hover:text-primary-foreground cursor-pointer transition-colors',
                  methods.watch('date') === format(dateSlot.date, 'dd/MM/yyyy') && 
                  methods.watch('heure') === time
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-primary-100 text-secondary'
                )}
              >
                {time}
              </li>
            ))}
          </ul>
        </Disclosure>
      ))}

      <ErrorMessage
        name="date"
        errors={methods.formState.errors}
        render={({ message }) => <TextError>{message}</TextError>}
      />
    </div>
  );
}
